import { Heading } from '@/components/ui/heading';
import Text from '@/components/ui/text';
import { cn } from '@/lib/utils';
import { Dialog as BaseDialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'framer-motion';
import * as React from 'react';

export interface DialogProps extends React.PropsWithChildren {
    content?: React.ReactNode;
    overlayClosable?: boolean;
    open?: boolean;
    showClose?: boolean;
    onClose?: () => void;
    onOpenChange?: (open: boolean) => void;
    onFocus?: React.FocusEventHandler<HTMLElement>;
}

export const Dialog = ({
    content,
    overlayClosable,
    open,
    showClose,
    onClose,
    children,
    onOpenChange,
    onFocus,
}: DialogProps) => {
    const initialFocusRef = React.useRef<HTMLButtonElement>(null);
    return (
        <AnimatePresence>
            <BaseDialog
                open={open ?? false}
                className="fixed inset-0 z-50 overflow-y-auto"
                initialFocus={initialFocusRef}
                onClose={(value) => {
                    onOpenChange?.(value);
                    if (overlayClosable) onClose?.();
                }}
                onFocus={onFocus}
                transition
            >
                <DialogBackdrop
                    as={motion.div}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-0 bg-neutral-900/40"
                    transition
                />
                <motion.div
                    transition={{ duration: 0.5 }}
                    className="flex min-h-screen items-center justify-center sm:p-4"
                >
                    <motion.div
                        transition={{ duration: 0.1 }}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className={cn(
                            'relative z-50 inline-block w-auto transform text-left',
                            'align-middle max-sm:h-screen max-sm:w-screen',
                        )}
                    >
                        <DialogPanel className="h-dvh sm:h-full max-h-dvh w-full">
                            <div className="bg-white shadow-xl overflow-hidden h-full sm:rounded-md">
                                {showClose ? (
                                    <button
                                        className="absolute right-4 top-3 z-10 flex items-center justify-center rounded-lg h-8 w-8 text-neutral-400 transition-colors hover:bg-neutral-500/10 hover:text-neutral-500 active:bg-neutral-500/20"
                                        onClick={() => {
                                            onClose?.();
                                        }}
                                    >
                                        <XMarkIcon className="h-5 w-5 text-neutral-700" />
                                    </button>
                                ) : null}
                                {content}
                                {children}
                            </div>
                        </DialogPanel>
                    </motion.div>
                </motion.div>
            </BaseDialog>
        </AnimatePresence>
    );
};

interface DialogContentProps extends React.PropsWithChildren {
    className?: string;
}

const DialogContent = ({ children, className }: DialogContentProps) => {
    return <div className={cn('flex w-screen max-w-sm flex-col gap-3 p-4', className)}>{children}</div>;
};

interface DialogHeaderProps extends React.PropsWithChildren {
    className?: string;
}

const DialogHeader = ({ children, className }: DialogHeaderProps) => {
    return (
        <div className={cn('flex flex-row gap-2 border-neutral-200 bg-white px-4 pt-3', className)}>
            {children}
        </div>
    );
};

const DialogIcon = ({ children }: React.PropsWithChildren) => {
    return <div className="[&>svg]:mt-0.5 [&>svg]:h-4 [&>svg]:w-4">{children}</div>;
};

const DialogHeaderText = ({ children }: React.PropsWithChildren) => {
    return <div className="flex flex-col gap-2">{children}</div>;
};

const DialogTitle = ({ children }: React.PropsWithChildren) => {
    return (
        <Heading variant="h1" className="text-sm font-semibold text-neutral-900">
            {children}
        </Heading>
    );
};

const DialogDescription = ({ children }: React.PropsWithChildren) => {
    return (
        <Text color={'neutral-600'} className="text-muted-foreground">
            {children}
        </Text>
    );
};

interface DialogActionsProps extends React.PropsWithChildren {
    className?: string;
}

const DialogActions = ({ children, className }: DialogActionsProps) => {
    return (
        <div className={cn('align-center flex w-full items-center justify-end gap-3', className)}>
            {children}
        </div>
    );
};

Dialog.Content = DialogContent;
Dialog.Header = DialogHeader;
Dialog.Icon = DialogIcon;
Dialog.HeaderText = DialogHeaderText;
Dialog.Title = DialogTitle;
Dialog.Description = DialogDescription;
Dialog.Actions = DialogActions;

export default Dialog;
